import React, { useCallback } from 'react'
import type { TablePaginationConfig } from 'antd/es/table'
import type {
  SorterResult,
  TableCurrentDataSource,
} from 'antd/es/table/interface'
import isNil from 'lodash/isNil'
import {
  SortDirection,
  UserListRow,
  UserListSortField,
} from '../../api/dto/user-invitation.dto'
import { ROW_CONTROL_SELECTOR } from '../../constants'

interface UseUsersTableInteractionsParams {
  /** How many users match the filters, which is what select-all offers. */
  total: number
  selectedEmails: string[]
  onSelectionChange: (emails: string[]) => void
  onSelectAllFiltered: () => void
  onClearSelection: () => void
  onSortChange: (
    field: UserListSortField | null,
    dir: SortDirection | null
  ) => void
  onOpenHistory: (row: UserListRow) => void
}

/** Wires the roster's antd table to the page: sorting, selection, row clicks. */
export const useUsersTableInteractions = ({
  total,
  selectedEmails,
  onSelectionChange,
  onSelectAllFiltered,
  onClearSelection,
  onSortChange,
  onOpenHistory,
}: UseUsersTableInteractionsParams) => {
  const handleTableChange = useCallback(
    (
      _pagination: TablePaginationConfig,
      _filters: unknown,
      sorter: SorterResult<UserListRow> | SorterResult<UserListRow>[],
      extra: TableCurrentDataSource<UserListRow>
    ) => {
      // antd sends a sorter with a columnKey on every change, page clicks
      // included, so only a sort action may reset the sort (and the page).
      if (extra.action !== 'sort') return
      const next = Array.isArray(sorter) ? sorter[0] : sorter
      if (!next?.columnKey) return
      if (!next.order) {
        onSortChange(null, null)
        return
      }
      onSortChange(
        next.columnKey as UserListSortField,
        next.order === 'ascend' ? 'asc' : 'desc'
      )
    },
    [onSortChange]
  )

  // Every row is selectable. A row that cannot be acted on is not hidden behind
  // a disabled checkbox with no explanation; it is listed in the confirm step
  // under "Will be skipped" with the reason.
  const rowSelection = {
    selectedRowKeys: selectedEmails,
    onChange: (keys: React.Key[]) => onSelectionChange(keys as string[]),
    preserveSelectedRowKeys: true,
    // Rides with the frozen Email column, so a row can still be ticked once
    // the table is scrolled right.
    fixed: true,
    // The header checkbox can only reach the rows it was sent, so acting on a
    // whole filtered status needs the server's answer instead.
    selections: [
      {
        key: 'all-filtered',
        text: `Select all ${total} matching the filters`,
        onSelect: onSelectAllFiltered,
      },
      {
        key: 'none',
        text: 'Clear selection',
        onSelect: onClearSelection,
      },
    ],
  }

  const handleRow = useCallback(
    (record: UserListRow) => ({
      onClick: (event: React.MouseEvent<HTMLElement>) => {
        // Controls inside the row own their own clicks: the select checkbox,
        // the company popover, a filter dropdown rendered over the row.
        if ((event.target as HTMLElement).closest(ROW_CONTROL_SELECTOR)) return
        // Reading an address by selecting it should not also open the drawer.
        const selection = window.getSelection()
        if (!isNil(selection) && selection.toString().length > 0) return
        onOpenHistory(record)
      },
    }),
    [onOpenHistory]
  )

  return { rowSelection, handleRow, handleTableChange }
}
